import Vue from 'vue'
import api from '../../services/api'
import { uuid } from './helpers'

export const callError = error => ({
  NotAllowedError: 'Autorisez le microphone et la caméra dans votre navigateur.',
  NotFoundError: 'Microphone ou caméra introuvable. Vérifiez vos appareils.',
  NotReadableError: 'Un appareil est déjà utilisé ou indisponible. Essayez un appel audio.',
  InvalidModificationError: 'Ce navigateur ne peut pas changer cette source vidéo pendant l’appel.'
})[error?.name] || error?.response?.data?.message || error?.message || 'Impossible de poursuivre cet appel.'

/** One media owner per browser tab. Reverb wakes polling; signaling is replayable. */
export class ChatCallSession {
  constructor({ transport = api, browser = window, onFocus = () => {}, onEnd = () => {}, onError = () => {} } = {}) {
    this.api = transport; this.browser = browser; this.onFocus = onFocus; this.onEnd = onEnd; this.onError = onError
    this.clientId = uuid(); this.generation = 0; this.cursor = 0; this.disposed = false
    this.pc = null; this.localStream = null; this.screenStream = null
    this.state = Vue.observable({ call:null, phase:'idle', busy:false, microphone:true, camera:false, screen:false,
      localVideo:null, localScreen:null, remoteVideo:null, remoteScreen:null, remoteAudio:null,
      remoteMedia:{microphone:true,camera:false,screen:false}, error:'', rtc:null,
      supported:!!(browser.isSecureContext && browser.RTCPeerConnection && browser.navigator?.mediaDevices?.getUserMedia),
      screenSupported:!!(browser.isSecureContext && browser.navigator?.mediaDevices?.getDisplayMedia) })
  }

  startPolling() {
    const tick = async () => { if(this.disposed) return; await this.sync(); if(!this.disposed) this.timer=setTimeout(tick,this.state.call && this.state.phase!=='ended' ? 2000 : 10000) }
    tick()
  }

  async sync() {
    if(this.disposed) return
    if(this.syncing) { this.resync=true; return }
    this.syncing=true
    const generation=this.generation
    const current=this.state.call
    try {
      const known=current && current.status!=='ended'
      const {data}=await this.api.get(known ? `chat/calls/${current.id}` : 'chat/calls/active',{timeout:10000,params:{client_id:this.clientId,after:this.cursor}})
      if(this.disposed || generation!==this.generation) return
      await this.apply(data)
      if(this.disposed || generation!==this.generation) return
      const call=this.state.call
      if(call?.owned && call.status!=='ended' && Date.now()-(this.lastHeartbeat || 0)>8000) {
        this.lastHeartbeat=Date.now()
        await this.api.post(`chat/calls/${call.id}/heartbeat`,{client_id:this.clientId,connected:this.pc?.connectionState==='connected'},{timeout:10000})
      }
    } catch(error) {
      if(this.disposed || generation!==this.generation) return
      if([403,404,409].includes(error.response?.status) && this.state.call) this.localEnd('L’appel n’est plus disponible dans cette session.')
      else if(error.response?.status!==401) this.state.error='La connexion à l’appel est momentanément indisponible.'
    } finally {
      this.syncing=false
      if(this.resync && !this.disposed) { this.resync=false; this.sync() }
    }
  }

  apply(data) {
    const generation=this.generation
    const apply=()=>{ if(!this.disposed && generation===this.generation) return this.applyNow(data) }
    this.applying=(this.applying || Promise.resolve()).then(apply,apply)
    return this.applying
  }

  async applyNow(data) {
    const call=data.call
    if(!call) { if(this.state.call && this.state.call.status!=='ended') this.localEnd(); return }
    if(this.state.call?.id===call.id && call.version<this.state.call.version) return
    if(this.state.call && this.state.call.id!==call.id) { this.clearMedia(); this.cursor=0 }
    this.state.call=call
    this.state.rtc=data.rtc || this.state.rtc
    if(call.status==='ended') {
      const labels={declined:'Appel refusé.',missed:'Aucune réponse.',cancelled:'Appel annulé.',timeout:'Appel interrompu après une perte de connexion.',failed:'La connexion de l’appel a échoué.'}
      this.localEnd(labels[call.end_reason] || '',call)
      return
    }
    if(call.status==='ringing') {
      this.state.phase='ringing'; this.state.remoteMedia.camera=call.mode==='video'; this.ring()
    } else {
      this.stopRing()
      if(!call.owned) { this.clearMedia(); this.state.phase='elsewhere'; return }
      if(!this.localStream) { await this.end('failed'); return }
      if(!this.pc) {
        const generation=this.generation
        try { await this.makePeer() }
        catch(error) { if(generation===this.generation && this.state.call?.id===call.id) { this.onError(callError(error)); await this.end('failed') } return }
      }
      for(const signal of data.signals || []) {
        if(this.state.call?.id!==call.id || this.state.call.status==='ended') return
        if(signal.id<=this.cursor) continue
        try { await this.receiveSignal(signal); this.cursor=signal.id }
        catch(error) { this.onError(callError(error)); await this.end('failed'); return }
      }
    }
  }

  async capture(video, generation) {
    const stream=await this.browser.navigator.mediaDevices.getUserMedia({audio:{echoCancellation:true,noiseSuppression:true},video:video ? {width:{ideal:640},height:{ideal:360}} : false})
    if(this.disposed || generation!==this.generation) { stream.getTracks().forEach(track=>track.stop()); return false }
    this.localStream=stream; this.state.camera=stream.getVideoTracks().length>0; this.state.microphone=true
    this.state.localVideo=this.state.camera ? new this.browser.MediaStream(stream.getVideoTracks()) : null
    return true
  }

  async start(conversationId, mode) {
    if(this.state.busy || (this.state.call && this.state.call.status!=='ended')) return
    if(!this.state.supported) { this.onError('Les appels nécessitent HTTPS ou localhost, et un navigateur compatible.'); return }
    this.clearMedia(); this.state.call=null; this.cursor=0; this.state.busy=true; this.state.phase='preparing'; this.state.error=''
    const generation=this.generation, id=uuid()
    try {
      if(!await this.capture(mode==='video',generation)) return
      const {data}=await this.api.post('chat/calls',{conversation_id:conversationId,mode,client_id:this.clientId,client_uuid:id},{timeout:10000})
      if(this.disposed || generation!==this.generation) { this.api.post(`chat/calls/${id}/end`,{client_id:this.clientId}).catch(()=>{}); return }
      await this.apply(data); this.onFocus(data.call)
    } catch(error) {
      if(generation===this.generation && !this.disposed) { this.clearMedia(); this.state.phase='idle'; this.onError(callError(error)) }
    } finally { if(!this.disposed) this.state.busy=false }
  }

  async accept(audioOnly = false) {
    const call=this.state.call
    if(!call || call.is_caller || call.status!=='ringing' || this.state.busy) return
    if(!this.state.supported) { this.onError('Ce navigateur ne permet pas les appels ici. Utilisez HTTPS ou localhost.'); return }
    this.state.busy=true; this.state.error=''; const generation=this.generation
    try {
      if(!await this.capture(call.mode==='video' && !audioOnly,generation)) return
      const {data}=await this.api.post(`chat/calls/${call.id}/accept`,{client_id:this.clientId},{timeout:10000})
      if(this.disposed || generation!==this.generation || this.state.call?.id!==call.id) return
      await this.apply(data); this.onFocus(data.call)
    } catch(error) {
      if(generation===this.generation && !this.disposed) { this.clearMedia(); this.onError(callError(error)); await this.sync() }
    } finally { if(!this.disposed) this.state.busy=false }
  }

  async makePeer() {
    const call=this.state.call, generation=this.generation
    const pc=this.pc=new this.browser.RTCPeerConnection({iceServers:this.state.rtc?.iceServers || [],iceTransportPolicy:this.state.rtc?.iceTransportPolicy || 'all'})
    this.state.phase='connecting'; this.pendingCandidates=[]
    this.connectTimer=setTimeout(()=>{ if(this.pc===pc && pc.connectionState!=='connected') { this.onError('Connexion impossible. Vérifiez le relais TURN ou réessayez sur un autre réseau.'); this.end('failed') } },30000)
    pc.onicecandidate=event=>{ if(event.candidate && this.pc===pc) this.send('candidate',event.candidate.toJSON()).catch(error=>{ if(this.pc===pc) this.state.error=callError(error) }) }
    pc.ontrack=event=>{
      if(this.pc!==pc) return
      const stream=new this.browser.MediaStream([event.track])
      if(event.track.kind==='audio') this.state.remoteAudio=stream
      else if(event.transceiver.mid==='2') this.state.remoteScreen=stream
      else this.state.remoteVideo=stream
    }
    pc.onconnectionstatechange=()=>{
      if(this.pc!==pc) return
      clearTimeout(this.disconnectTimer)
      if(pc.connectionState==='connected') { clearTimeout(this.connectTimer); this.state.phase='connected'; this.state.error=''; this.lastHeartbeat=0; this.sync() }
      else if(pc.connectionState==='disconnected') { this.state.phase='reconnecting'; this.disconnectTimer=setTimeout(()=>{ if(this.pc===pc && pc.connectionState!=='connected') this.end('failed') },20000) }
      else if(pc.connectionState==='failed') this.end('failed')
    }
    if(call.is_caller) {
      this.transceivers=[pc.addTransceiver('audio',{direction:'sendrecv'}),pc.addTransceiver('video',{direction:'sendrecv'}),pc.addTransceiver('video',{direction:'sendrecv'})]
      await this.attachTracks(pc)
      const offer=await pc.createOffer()
      if(this.pc!==pc || generation!==this.generation) return
      await pc.setLocalDescription(offer)
      if(this.pc!==pc) return
      await this.send('offer',{type:pc.localDescription.type,sdp:pc.localDescription.sdp})
    }
    if(this.pc===pc) await this.sendMedia()
  }

  async attachTracks(pc) {
    const transceivers=pc.getTransceivers()
    this.transceivers=transceivers
    const tracks=[this.localStream?.getAudioTracks()[0],this.localStream?.getVideoTracks()[0],this.screenStream?.getVideoTracks()[0]]
    for(let index=0;index<transceivers.length;index++) {
      if(this.pc!==pc) return
      transceivers[index].direction='sendrecv'
      await transceivers[index].sender.replaceTrack(tracks[index] || null)
    }
  }

  async receiveSignal({kind,payload}) {
    const pc=this.pc
    if(!pc) return
    if(kind==='media') { this.state.remoteMedia={...payload}; return }
    if(kind==='candidate') {
      if(!pc.remoteDescription) this.pendingCandidates.push(payload)
      else await pc.addIceCandidate(payload)
      return
    }
    if(kind==='offer') {
      if(!pc.remoteDescription) {
        await pc.setRemoteDescription(payload)
        if(this.pc!==pc) return
        await this.attachTracks(pc)
        const answer=await pc.createAnswer()
        if(this.pc!==pc) return
        await pc.setLocalDescription(answer)
      }
      if(this.pc!==pc) return
      await this.send('answer',{type:pc.localDescription.type,sdp:pc.localDescription.sdp})
    } else if(kind==='answer' && !pc.remoteDescription) await pc.setRemoteDescription(payload)
    if(this.pc!==pc) return
    for(const candidate of this.pendingCandidates.splice(0)) { if(this.pc!==pc) return; await pc.addIceCandidate(candidate) }
  }

  async send(kind, payload) {
    const call=this.state.call
    if(!call?.owned || call.status!=='accepted' || this.disposed) return
    const generation=this.generation, clientUuid=uuid()
    const body={client_id:this.clientId,client_uuid:clientUuid,kind,payload}
    for(let attempt=0;attempt<2;attempt++) {
      if(generation!==this.generation || this.disposed) return
      try { await this.api.post(`chat/calls/${call.id}/signals`,body,{timeout:10000}); return }
      catch(error) { if(attempt || error.response?.status) throw error; await new Promise(resolve=>setTimeout(resolve,350)) }
    }
  }

  sendMedia() { return this.send('media',{microphone:this.state.microphone,camera:this.state.camera,screen:this.state.screen}) }
  async microphone() {
    if(!this.localStream) return
    this.state.microphone=!this.state.microphone
    this.localStream.getAudioTracks().forEach(track=>{ track.enabled=this.state.microphone })
    try { await this.sendMedia() } catch(error) { this.state.error=callError(error) }
  }

  async camera() {
    if(!this.pc || this.transceivers?.length!==3 || this.state.busy) return
    this.state.busy=true; const generation=this.generation, pc=this.pc
    try {
      if(this.state.camera) {
        await this.transceivers[1].sender.replaceTrack(null)
        this.localStream.getVideoTracks().forEach(track=>{ track.stop(); this.localStream.removeTrack(track) })
        this.state.localVideo=null; this.state.camera=false
      } else {
        const stream=await this.browser.navigator.mediaDevices.getUserMedia({audio:false,video:{width:{ideal:640},height:{ideal:360}}})
        if(this.pc!==pc || generation!==this.generation) { stream.getTracks().forEach(track=>track.stop()); return }
        const track=stream.getVideoTracks()[0]
        try { await this.transceivers[1].sender.replaceTrack(track) } catch(error) { stream.getTracks().forEach(t=>t.stop()); throw error }
        if(this.pc!==pc) { stream.getTracks().forEach(t=>t.stop()); return }
        this.localStream.addTrack(track); this.state.localVideo=stream; this.state.camera=true
      }
      await this.sendMedia()
    } catch(error) { if(generation===this.generation) this.onError(callError(error)) }
    finally { if(generation===this.generation) this.state.busy=false }
  }

  async screen() {
    if(!this.pc || this.transceivers?.length!==3 || !this.state.screenSupported || this.state.busy) return
    if(this.state.screen) { await this.stopScreen(); return }
    // getDisplayMedia is invoked directly from the click: no awaited API before permission.
    const pending=this.browser.navigator.mediaDevices.getDisplayMedia({video:{frameRate:{ideal:15,max:30}},audio:false})
    const generation=this.generation, pc=this.pc
    this.state.busy=true
    try {
      const stream=await pending
      if(this.pc!==pc || generation!==this.generation) { stream.getTracks().forEach(track=>track.stop()); return }
      const track=stream.getVideoTracks()[0]
      try { await this.transceivers[2].sender.replaceTrack(track) } catch(error) { stream.getTracks().forEach(t=>t.stop()); throw error }
      if(this.pc!==pc) { stream.getTracks().forEach(t=>t.stop()); return }
      this.screenStream=stream; this.state.localScreen=stream; this.state.screen=true
      track.onended=()=>{ if(this.screenStream===stream) this.stopScreen() }
      await this.sendMedia()
    } catch(error) { if(generation===this.generation && error.name!=='NotAllowedError') this.onError(callError(error)) }
    finally { if(generation===this.generation) this.state.busy=false }
  }

  async stopScreen() {
    const stream=this.screenStream; this.screenStream=null
    stream?.getTracks().forEach(track=>{ track.onended=null; track.stop() })
    this.state.screen=false; this.state.localScreen=null
    try { if(this.pc && this.transceivers?.[2]) await this.transceivers[2].sender.replaceTrack(null); await this.sendMedia() }
    catch(error) { if(this.pc) this.state.error=callError(error) }
  }

  async end(reason = 'hangup') {
    const call=this.state.call
    const canEnd=call && call.status!=='ended' && (call.owned || (!call.is_caller && call.status==='ringing'))
    this.localEnd('',call ? {...call,status:'ended'} : null)
    if(canEnd) {
      try { await this.api.post(`chat/calls/${call.id}/end`,{client_id:this.clientId,reason},{timeout:10000}); if(!this.disposed) this.onEnd(call) }
      catch(error) { if(!this.disposed && ![403,404,409].includes(error.response?.status)) this.onError('L’appel est fermé ici. Le serveur libérera la session dès confirmation ou expiration.') }
    }
  }

  localEnd(message = '', call = this.state.call) {
    const alreadyEnded=this.state.phase==='ended' && this.state.call?.id===call?.id
    this.clearMedia(); this.state.call=call ? {...call,status:'ended'} : null; this.state.phase=call ? 'ended' : 'idle'; this.state.busy=false
    this.state.error=message
    if(call && !alreadyEnded && !this.disposed) this.onEnd(call)
  }

  clearMedia() {
    this.generation++; this.stopRing(); clearTimeout(this.connectTimer); clearTimeout(this.disconnectTimer)
    if(this.pc) { this.pc.ontrack=null; this.pc.onicecandidate=null; this.pc.onconnectionstatechange=null; this.pc.close(); this.pc=null }
    for(const stream of [this.localStream,this.screenStream,this.state.remoteAudio,this.state.remoteVideo,this.state.remoteScreen]) stream?.getTracks().forEach(track=>{ track.onended=null; track.stop() })
    this.localStream=null; this.screenStream=null; this.transceivers=null; this.pendingCandidates=[]; this.lastHeartbeat=0
    Object.assign(this.state,{localVideo:null,localScreen:null,remoteAudio:null,remoteVideo:null,remoteScreen:null,camera:false,screen:false,remoteMedia:{microphone:true,camera:false,screen:false}})
  }

  ring() {
    if(this.ringTimer || this.disposed) return
    const AudioContext=this.browser.AudioContext || this.browser.webkitAudioContext
    if(!AudioContext) return
    try {
      this.audioContext=new AudioContext(); this.audioContext.resume().catch(()=>{})
      const tone=()=>{
        if(!this.audioContext || this.audioContext.state!=='running') return
        const context=this.audioContext, oscillator=context.createOscillator(), gain=context.createGain()
        oscillator.frequency.value=440; gain.gain.setValueAtTime(0,context.currentTime); gain.gain.linearRampToValueAtTime(.06,context.currentTime+.03); gain.gain.linearRampToValueAtTime(0,context.currentTime+.45)
        oscillator.connect(gain); gain.connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime+.5)
        oscillator.onended=()=>{ oscillator.disconnect(); gain.disconnect() }
      }
      tone(); this.ringTimer=setInterval(tone,2200)
    } catch(error) { /* Browser autoplay restrictions must not suppress the visual invitation. */ }
  }
  stopRing() { clearInterval(this.ringTimer); this.ringTimer=null; this.audioContext?.close().catch(()=>{}); this.audioContext=null }
  destroy() { this.disposed=true; clearTimeout(this.timer); this.end().catch(()=>{}); this.clearMedia() }
}
